import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Dialog,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useGetUserRoleQuery,
  useGetRestaurantQuery,
  useGetTeamQuery,
  useCreateUserStaffMutation,
} from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import { validateEmail } from "../../../Components/Validation/validationUtils";
import { useNavigate } from "react-router-dom";
import {
  IRestaurant,
  IUserRole,
  ITeam,
  CustomerOption,
  IUserAdmin,
} from "../../../Api/Interface/api.interface";
import NewUserRole from "../../UserRole/Edit/index";
import AddIcon from "@mui/icons-material/Add";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { BiSolidMessageSquareAdd } from "react-icons/bi";

interface FormValues {
  username: string;
  user_role: string;
  branchIds: number[];
  staffId: string;
  password: string;
}

interface IUserAdminPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  getStaffId: string;
}

const UserAdmin = ({
  openModel = false,
  handleCloseDialog,
  getStaffId,
}: IUserAdminPopUp) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [openDepartment, setOpenDepartment] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [newCreate, { isLoading: createIsLoading }] = useCreateUserStaffMutation();
  const [showPassword, setShowPassword] = useState(false);

  const [open] = React.useState(openModel);
  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetUserRoleQuery({
      searchText: "",
    });

  const { data: restaurantData, isLoading: restauramtIsLoading } =
    useGetRestaurantQuery({
      searchText: "",
      id: "",
    });

  const { data: staffData, isLoading: staffIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const userroleList = useMemo(() => {
    return userroleData?.data as IUserRole[];
  }, [userroleData?.data]);

  const restaurantList = useMemo(() => {
    return restaurantData?.data as IRestaurant[];
  }, [restaurantData?.data]);

  const staffList = useMemo(() => {
    return staffData?.data as ITeam[];
  }, [staffData?.data]);

  const staffOptions = useMemo(() => {
    if (!Array.isArray(staffList)) return [];
    return staffList.map((cust) => ({
      label: `${capitalizeFirstLetter(
        cust.teamFirstName
      )} ${capitalizeFirstLetter(cust.teamLastName)}`,
      value: cust.id,
    }));
  }, [staffList]);

  const userRoleOptions = useMemo(() => {
    if (!Array.isArray(userroleList)) return [];
    return userroleList.map((cust) => ({
      label: `${capitalizeFirstLetter(cust.roleName)}`,
      value: Number(cust.id),
    }));
  }, [userroleList]);

  const formik = useFormik<FormValues>({
    initialValues: {
      username: "",
      password: "",
      user_role: "",
      branchIds: [],
      staffId: getStaffId,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.username);
      if (emailError) {
        errors.username = emailError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const createData = {
          username: values.username,
          user_role: values.user_role,
          staffId: values.staffId,
          branchIds: selectedCategories,
          password: values.password,
        };
        
        const response = await newCreate(createData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
          setSelectedCategories([]);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedCategories(
        restaurantList.map((category) => Number(category.id))
      );
    } else {
      setSelectedCategories([]);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const formValid = useMemo(() => {
    return (
      formik.values.username !== "" &&
      formik.values.username !== undefined &&
      formik.values.user_role !== "" &&
      formik.values.user_role !== undefined &&
      formik.values.password !== "" &&
      formik.values.password !== undefined &&
      formik.values.staffId !== "" &&
      formik.values.staffId !== undefined 
      // selectedCategories.length > 0
    );
  }, [
    formik.values.username,
    formik.values.user_role,
    formik.values.staffId,
    formik.values.password,
    // selectedCategories,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                  Create
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            User Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="User Name"
                                size="small"
                                {...formik.getFieldProps("username")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                error={
                                  formik.touched.username &&
                                  Boolean(formik.errors.username)
                                }
                                helperText={
                                  formik.touched.username &&
                                  formik.errors.username
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Role{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={10.5} md={10.5} sm={10.5} xs={10.5}>
                              <Autocomplete
                                options={userRoleOptions}
                                loading={userroleIsLoading}
                                getOptionLabel={(option: CustomerOption) =>
                                  option.label
                                }
                                value={
                                  userRoleOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      Number(formik.values.user_role)
                                  ) || null
                                }
                                onChange={(
                                  _,
                                  newValue: CustomerOption | null
                                ) => {
                                  formik.setFieldValue(
                                    "user_role",
                                    newValue?.value || ""
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="User Role"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    className="textField"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {userroleIsLoading ? (
                                            <CircularProgress size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                sx={{ width: "100%" }}
                              />
                            </Grid>
                            <Grid
                              item
                              lg={1.5}
                              md={1.5}
                              sm={1.5}
                              xs={1.5}
                              display="flex"
                              sx={{
                                justifyContent: "flex-end",
                                alignItems: "flex-end",
                                textAlign: "end",
                              }}
                            >
                              <Button
                                variant="contained"
                                className={classes.editButton}
                                onClick={() => {
                                  setOpenDepartment(true);
                                }}
                              >
                                <AddIcon />
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Password{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Password"
                            size="small"
                            type={showPassword ? "text" : "password"}
                            {...formik.getFieldProps("password")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                  >
                                    {showPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Select Branch
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid container spacing={3} p={"24px"}>
                          {restauramtIsLoading ? (
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "200px",
                              }}
                            >
                              <CircularProgress />
                            </Grid>
                          ) : (
                            <>
                              {/* Select All Checkbox */}
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        selectedCategories.length ===
                                        restaurantList.length
                                      }
                                      onChange={handleSelectAllChange}
                                    />
                                  }
                                  label="Select All"
                                />
                              </Grid>

                              {restaurantList.map((category: IRestaurant) => (
                                <Grid
                                  item
                                  lg={4}
                                  md={4}
                                  sm={12}
                                  xs={12}
                                  key={category.id}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedCategories.includes(
                                          Number(category.id)
                                        )}
                                        onChange={() =>
                                          handleCategoryChange(
                                            Number(category.id)
                                          )
                                        }
                                      />
                                    }
                                    label={category.restaurantsName}
                                  />
                                </Grid>
                              ))}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              createIsLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <BiSolidMessageSquareAdd />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || createIsLoading}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                {openDepartment && (
                  <NewUserRole
                    handleCloseDialog={() => setOpenDepartment(false)}
                    openModel={openDepartment}
                  />
                )}
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default UserAdmin;
